import { STATE_OPTIONS } from '../../../../constants/address'

// Quick-add holiday sets for the Special Hours card (ACD-15249 Q3). The set is
// picked from the location's province/state; anything unrecognized falls back
// to the Canadian federal set. Dates are the actual calendar date — weekend
// "observed" shifts are not applied (the admin can edit the date).

export type CanadianProvince =
  | 'AB'
  | 'BC'
  | 'MB'
  | 'NB'
  | 'NL'
  | 'NS'
  | 'NT'
  | 'NU'
  | 'ON'
  | 'PE'
  | 'QC'
  | 'SK'
  | 'YT'

export type HolidayRegion = CanadianProvince | 'CA-FED' | 'US-FED'

export interface HolidayRule {
  name: string
  // false = common closing day that is not a statutory holiday in the region
  statutory: boolean
  // Date of the holiday in `year`, as `YYYY-MM-DD`
  at: (year: number) => string
}

export interface Holiday {
  name: string
  date: string
  statutory: boolean
}

// ---------- date-rule helpers (month is 1-12, weekday 0 = Sunday) ----------

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`
}

function weekdayOf(year: number, month: number, day: number): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

export function nthWeekday(
  year: number,
  month: number,
  weekday: number,
  n: number,
): string {
  const offset = (weekday - weekdayOf(year, month, 1) + 7) % 7
  return ymd(year, month, 1 + offset + 7 * (n - 1))
}

export function lastWeekday(
  year: number,
  month: number,
  weekday: number,
): string {
  const last = daysInMonth(year, month)
  const offset = (weekdayOf(year, month, last) - weekday + 7) % 7
  return ymd(year, month, last - offset)
}

// Victoria Day / National Patriots' Day: the Monday before May 25.
export function mondayBeforeMay25(year: number): string {
  const offset = (weekdayOf(year, 5, 24) - 1 + 7) % 7
  return ymd(year, 5, 24 - offset)
}

// Easter Sunday (anonymous Gregorian algorithm), as `YYYY-MM-DD`.
export function easter(year: number): string {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return ymd(year, month, day)
}

function goodFriday(year: number): string {
  const [y, m, d] = easter(year).split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d - 2))
  return ymd(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate())
}

// ---------- rules ----------

const MON = 1
const THU = 4

const fixed =
  (month: number, day: number) =>
  (year: number): string =>
    ymd(year, month, day)

function stat(name: string, at: (year: number) => string): HolidayRule {
  return { name, statutory: true, at }
}

const NEW_YEARS_DAY = stat("New Year's Day", fixed(1, 1))
const FAMILY_DAY = stat('Family Day', (y) => nthWeekday(y, 2, MON, 3))
const GOOD_FRIDAY = stat('Good Friday', goodFriday)
const VICTORIA_DAY = stat('Victoria Day', mondayBeforeMay25)
const CANADA_DAY = stat('Canada Day', fixed(7, 1))
const LABOUR_DAY = stat('Labour Day', (y) => nthWeekday(y, 9, MON, 1))
const TRUTH_AND_RECONCILIATION = stat(
  'National Day for Truth and Reconciliation',
  fixed(9, 30),
)
const THANKSGIVING = stat('Thanksgiving', (y) => nthWeekday(y, 10, MON, 2))
const REMEMBRANCE_DAY = stat('Remembrance Day', fixed(11, 11))
const CHRISTMAS_DAY = stat('Christmas Day', fixed(12, 25))
const BOXING_DAY = stat('Boxing Day', fixed(12, 26))
const INDIGENOUS_PEOPLES_DAY = stat(
  'National Indigenous Peoples Day',
  fixed(6, 21),
)
const CIVIC_HOLIDAY = stat('Civic Holiday', (y) => nthWeekday(y, 8, MON, 1))
const thirdMondayFeb = (name: string) =>
  stat(name, (y) => nthWeekday(y, 2, MON, 3))
const firstMondayAug = (name: string) =>
  stat(name, (y) => nthWeekday(y, 8, MON, 1))

const CHRISTMAS_EVE: HolidayRule = {
  name: 'Christmas Eve',
  statutory: false,
  at: fixed(12, 24),
}
const BOXING_DAY_EXTRA: HolidayRule = { ...BOXING_DAY, statutory: false }
const NEW_YEARS_EVE: HolidayRule = {
  name: "New Year's Eve",
  statutory: false,
  at: fixed(12, 31),
}

// Closing-day extras appended after the statutory set. Boxing Day is only an
// extra where it is not already statutory.
const EXTRAS = [CHRISTMAS_EVE, BOXING_DAY_EXTRA, NEW_YEARS_EVE]
const EXTRAS_NO_BOXING = [CHRISTMAS_EVE, NEW_YEARS_EVE]

// Statutory sets in calendar order. Review flagged entries with someone
// familiar with each jurisdiction (see plan ACD-15249 "Risks").
export const HOLIDAY_RULES: Record<HolidayRegion, HolidayRule[]> = {
  'CA-FED': [
    NEW_YEARS_DAY,
    GOOD_FRIDAY,
    CANADA_DAY,
    LABOUR_DAY,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  AB: [
    NEW_YEARS_DAY,
    FAMILY_DAY,
    GOOD_FRIDAY,
    VICTORIA_DAY,
    CANADA_DAY,
    LABOUR_DAY,
    THANKSGIVING,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  BC: [
    NEW_YEARS_DAY,
    FAMILY_DAY,
    GOOD_FRIDAY,
    VICTORIA_DAY,
    CANADA_DAY,
    firstMondayAug('BC Day'),
    LABOUR_DAY,
    TRUTH_AND_RECONCILIATION,
    THANKSGIVING,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  MB: [
    NEW_YEARS_DAY,
    thirdMondayFeb('Louis Riel Day'),
    GOOD_FRIDAY,
    VICTORIA_DAY,
    CANADA_DAY,
    LABOUR_DAY,
    TRUTH_AND_RECONCILIATION,
    THANKSGIVING,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  NB: [
    NEW_YEARS_DAY,
    FAMILY_DAY,
    GOOD_FRIDAY,
    CANADA_DAY,
    firstMondayAug('New Brunswick Day'),
    LABOUR_DAY,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  NL: [
    NEW_YEARS_DAY,
    GOOD_FRIDAY,
    stat('Memorial Day / Canada Day', fixed(7, 1)),
    LABOUR_DAY,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  NS: [
    NEW_YEARS_DAY,
    thirdMondayFeb('Heritage Day'),
    GOOD_FRIDAY,
    CANADA_DAY,
    LABOUR_DAY,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  NT: [
    NEW_YEARS_DAY,
    GOOD_FRIDAY,
    VICTORIA_DAY,
    INDIGENOUS_PEOPLES_DAY,
    CANADA_DAY,
    CIVIC_HOLIDAY,
    LABOUR_DAY,
    TRUTH_AND_RECONCILIATION,
    THANKSGIVING,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    BOXING_DAY,
    ...EXTRAS_NO_BOXING,
  ],
  NU: [
    NEW_YEARS_DAY,
    GOOD_FRIDAY,
    VICTORIA_DAY,
    CANADA_DAY,
    stat('Nunavut Day', fixed(7, 9)),
    CIVIC_HOLIDAY,
    LABOUR_DAY,
    TRUTH_AND_RECONCILIATION,
    THANKSGIVING,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    BOXING_DAY,
    ...EXTRAS_NO_BOXING,
  ],
  ON: [
    NEW_YEARS_DAY,
    FAMILY_DAY,
    GOOD_FRIDAY,
    VICTORIA_DAY,
    CANADA_DAY,
    LABOUR_DAY,
    THANKSGIVING,
    CHRISTMAS_DAY,
    BOXING_DAY,
    ...EXTRAS_NO_BOXING,
  ],
  PE: [
    NEW_YEARS_DAY,
    thirdMondayFeb('Islander Day'),
    GOOD_FRIDAY,
    CANADA_DAY,
    LABOUR_DAY,
    TRUTH_AND_RECONCILIATION,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  QC: [
    NEW_YEARS_DAY,
    GOOD_FRIDAY,
    stat("National Patriots' Day", mondayBeforeMay25),
    stat('Fête nationale', fixed(6, 24)),
    CANADA_DAY,
    LABOUR_DAY,
    THANKSGIVING,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  SK: [
    NEW_YEARS_DAY,
    FAMILY_DAY,
    GOOD_FRIDAY,
    VICTORIA_DAY,
    CANADA_DAY,
    firstMondayAug('Saskatchewan Day'),
    LABOUR_DAY,
    THANKSGIVING,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    ...EXTRAS,
  ],
  YT: [
    NEW_YEARS_DAY,
    GOOD_FRIDAY,
    VICTORIA_DAY,
    INDIGENOUS_PEOPLES_DAY,
    CANADA_DAY,
    stat('Discovery Day', (y) => nthWeekday(y, 8, MON, 3)),
    LABOUR_DAY,
    TRUTH_AND_RECONCILIATION,
    THANKSGIVING,
    REMEMBRANCE_DAY,
    CHRISTMAS_DAY,
    BOXING_DAY,
    ...EXTRAS_NO_BOXING,
  ],
  'US-FED': [
    NEW_YEARS_DAY,
    stat('Martin Luther King Jr. Day', (y) => nthWeekday(y, 1, MON, 3)),
    stat("Presidents' Day", (y) => nthWeekday(y, 2, MON, 3)),
    stat('Memorial Day', (y) => lastWeekday(y, 5, MON)),
    stat('Juneteenth', fixed(6, 19)),
    stat('Independence Day', fixed(7, 4)),
    stat('Labor Day', (y) => nthWeekday(y, 9, MON, 1)),
    stat("Columbus / Indigenous Peoples' Day", (y) =>
      nthWeekday(y, 10, MON, 2),
    ),
    stat('Veterans Day', fixed(11, 11)),
    stat('Thanksgiving', (y) => nthWeekday(y, 11, THU, 4)),
    CHRISTMAS_DAY,
    ...EXTRAS_NO_BOXING,
  ],
}

export function holidaysForYear(region: HolidayRegion, year: number): Holiday[] {
  return HOLIDAY_RULES[region].map((rule) => ({
    name: rule.name,
    date: rule.at(year),
    statutory: rule.statutory,
  }))
}

// ---------- region resolution ----------

function normalizeText(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toUpperCase()
}

const CA_COUNTRY = new Set(['CA', 'CAN', 'CANADA'])
const US_COUNTRY = new Set([
  'US',
  'USA',
  'UNITED STATES',
  'UNITED STATES OF AMERICA',
])

// Matches a normalized state value against a country's code + label list.
function matchState(country: 'CA' | 'US', state: string): string | null {
  if (!state) return null
  const options = STATE_OPTIONS[country] ?? []
  const hit = options.find(
    (o) => o.value === state || normalizeText(o.label) === state,
  )
  return hit ? hit.value : null
}

/**
 * Resolve the quick-add holiday set for a location from its stored
 * `country` / `state`. Accepts ISO codes, full names, any case and accents
 * (POS-synced / legacy data); anything unrecognized falls back to `CA-FED`.
 */
export function resolveHolidayRegion(
  country: string | null | undefined,
  state: string | null | undefined,
): HolidayRegion {
  const c = normalizeText(country)
  const s = normalizeText(state)

  if (CA_COUNTRY.has(c)) {
    return (matchState('CA', s) as CanadianProvince | null) ?? 'CA-FED'
  }
  if (US_COUNTRY.has(c)) return 'US-FED'

  // Unknown country: bare "CA" is ambiguous (Canada vs California).
  if (s === 'CA') return 'CA-FED'
  const province = matchState('CA', s) as CanadianProvince | null
  if (province) return province
  if (matchState('US', s)) return 'US-FED'
  return 'CA-FED'
}
